/**
 * NCR Detail — Section 1: Customer & Supplier.
 *
 * Extracted verbatim from NCRDetailTab for the 500-line file-size rule.
 */

import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { NonConformanceReport } from '../../../lib/api';
import { NCREditFormData } from '../types';
import { Section } from '../Section';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { companyNcrReference } from '../../../lib/ncrReference';

interface CustomerSupplierSectionProps {
  ncr: NonConformanceReport;
  formData: NCREditFormData;
  canEdit: boolean;
  onUpdateFormData: (field: string, value: unknown) => void;
}

export function CustomerSupplierSection({
  ncr,
  formData,
  canEdit,
  onUpdateFormData,
}: CustomerSupplierSectionProps) {
  const { formatDate } = useFormatters();

  return (
    <Section title="Customer & Supplier">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Customer</Label>
          <Input value={ncr.company_name} disabled />
        </div>
        <div>
          <Label>Supplier</Label>
          {/* Display-only: the supplier is fixed at creation. `readOnly`
              keeps React from warning about a value with no onChange once
              the field is enabled on a draft. */}
          <Input value={ncr.supplier_name} readOnly disabled={!canEdit} />
        </div>
        <div>
          <Label>Raised By</Label>
          <Input value={ncr.raised_by_name} disabled />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Company NCR/SRN No.</Label>
          {/* Blank (dash) when the customer gave no reference and the
              stored number is the system-issued NCR##### one. */}
          <Input value={companyNcrReference(ncr.ncr_number) || '—'} disabled />
        </div>
        <div>
          <Label>NCR Date</Label>
          <Input value={ncr.ncr_date ? formatDate(ncr.ncr_date) : '—'} disabled />
        </div>
        <div>
          <Label>Severity</Label>
          <select
            value={formData.severity || 'minor'}
            onChange={(e) => onUpdateFormData('severity', e.target.value)}
            disabled={!canEdit}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          >
            <option value="minor">Minor</option>
            <option value="major">Major</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>
    </Section>
  );
}
